/**
 * Build a CMS image URL with a stable version query param based on lastModified.
 * - Rewrites "/assets" to "/img/assets" to go through the Next.js rewrite → Statamic
 * - In development, uses same-origin `/img/assets/*` (route handler proxy) so images work
 *   in Cursor's embedded browser, which does not trust local Valet/Herd TLS certs
 * - Normalizes absolute Statamic URLs to pathname (+ search) so we never rewrite
 *   CMS hosts to bogus paths like ".../img/assets..." on the CMS server
 * - Normalizes lastModified to epoch seconds (integer)
 *
 * @param {string} permalink - CMS asset permalink (e.g., "/assets/..." or full URL)
 * @param {string|number} [lastModified] - Timestamp string (e.g., "2024-05-16 13:22:10") or epoch seconds/ms
 * @returns {string} URL path (or absolute URL) with optional `?v=` / `&v=`
 */
function statamicHostsMatch(hostname, configured) {
  if (!configured || !hostname) return false;
  const a = hostname.replace(/^www\./i, "").toLowerCase();
  const b = configured.replace(/^www\./i, "").toLowerCase();
  return a === b || a.endsWith(`.${b}`);
}

/** @returns {number|undefined} */
function parseLastModifiedToSeconds(lastModified) {
  if (typeof lastModified === "number") {
    return lastModified > 2000000000
      ? Math.floor(lastModified / 1000)
      : Math.floor(lastModified);
  }

  if (typeof lastModified !== "string") {
    return undefined;
  }

  const trimmed = lastModified.trim();

  if (/^\d+$/.test(trimmed)) {
    const asNumber = Number(trimmed);
    return asNumber > 2000000000
      ? Math.floor(asNumber / 1000)
      : Math.floor(asNumber);
  }

  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );
  if (match) {
    const [, y, m, d, hh, mm, ss] = match.map(Number);
    const ms = Date.UTC(y, m - 1, d, hh, mm, ss);
    return Math.floor(ms / 1000);
  }

  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? undefined : Math.floor(parsed / 1000);
}

/**
 * @param {string} base
 * @param {number} versionSeconds
 */
function appendCacheBust(base, versionSeconds) {
  const join = base.includes("?") ? "&" : "?";
  return `${base}${join}v=${versionSeconds}`;
}

/** Cloudflare rejects sources above 10,000px on a side or 100 megapixels. */
export function exceedsCloudflareTransformLimits(width, height) {
  if (typeof width !== "number" || typeof height !== "number") {
    return false;
  }
  if (width <= 0 || height <= 0) {
    return false;
  }
  return width > 10000 || height > 10000 || width * height > 100_000_000;
}

/**
 * @param {string} permalink
 * @param {string|number} [lastModified]
 * @param {{ width?: number, height?: number }} [dimensions]
 */
export function cmsImageUrl(permalink, lastModified, dimensions) {
  if (!permalink) return "";

  const statamicHost = process.env.NEXT_PUBLIC_STATAMIC_URL?.trim();

  /** Path or scheme-relative or absolute external URL ready for rewrite / passthrough */
  let pathOrUrl = permalink.trim();

  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const u = new URL(pathOrUrl);
      // Only strip the Statamic CMS host (paths like /assets/...).
      // Leave assets CDN URLs absolute — they are not under /assets/.
      if (
        statamicHost &&
        statamicHostsMatch(u.hostname, statamicHost)
      ) {
        pathOrUrl = `${u.pathname}${u.search}`;
      }
    } catch {
      // malformed URL — leave as-is
    }
  }

  // Already a CF transform URL — keep as-is
  if (pathOrUrl.includes("/cdn-cgi/image/")) {
    const versionSeconds = parseLastModifiedToSeconds(lastModified);
    if (versionSeconds != null) {
      return appendCacheBust(pathOrUrl, versionSeconds);
    }
    return pathOrUrl;
  }

  // Normalize /img/assets/* (same-origin proxy) back to /assets/* for rewrite
  if (pathOrUrl.startsWith("/img/assets/") || pathOrUrl === "/img/assets") {
    pathOrUrl =
      pathOrUrl === "/img/assets"
        ? "/assets"
        : `/assets${pathOrUrl.slice("/img/assets".length)}`;
  }

  const usesStatamicAssetPath =
    pathOrUrl.startsWith("/assets/") || pathOrUrl === "/assets";

  const assetsCdn = process.env.NEXT_PUBLIC_ASSETS_URL?.replace(/\/$/, "");
  const useGlide = exceedsCloudflareTransformLimits(
    dimensions?.width,
    dimensions?.height
  );

  // Production GraphQL returns absolute assets-CDN permalinks. Preserve those
  // for normal images, but suppress known oversized sources: Cloudflare rejects
  // them and `onerror=redirect` would otherwise send the full-resolution
  // original to the browser. An empty URL makes Img render its safe placeholder.
  let isOversizedAssetsCdnImage = false;
  if (assetsCdn && useGlide && /^https?:\/\//i.test(pathOrUrl)) {
    try {
      const assetUrl = new URL(pathOrUrl);
      const configuredAssetsUrl = new URL(assetsCdn);
      if (assetUrl.origin === configuredAssetsUrl.origin) {
        isOversizedAssetsCdnImage = true;
      }
    } catch {
      // malformed URL — retain the existing passthrough behaviour
    }
  }

  if (isOversizedAssetsCdnImage) {
    return "";
  }

  /** R2/CDN URL when configured; otherwise same-origin `/img/assets/*` proxy */
  let rewritten = pathOrUrl;
  if (usesStatamicAssetPath) {
    const assetPath =
      pathOrUrl === "/assets" ? "" : pathOrUrl.slice("/assets".length);
    if (assetsCdn && !useGlide) {
      rewritten = `${assetsCdn}${assetPath}`;
    } else {
      rewritten = `/img/assets${assetPath}`;
    }
  }

  const versionSeconds = parseLastModifiedToSeconds(lastModified);

  if (versionSeconds != null) {
    return appendCacheBust(rewritten, versionSeconds);
  }
  return rewritten;
}
